import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { fetchTerminal, fetchCategories, getMyTerminals, recordLaunch, getWorkspace } from "../lib/api";
import { useAuthUser } from "../lib/useAuthUser";
import { usePWAInstall, isStandalone, useOpenMode, setOpenMode, openManagedWindows, popupHelp, POPUP_TOAST_DURATION } from "../lib/pwa";
import TerminalMenuBar from "./TerminalMenuBar";

const ACTIVE_KEY = "tv_active_terminal_id";
// The standalone PWA / Terminal screen persists its terminal under this key.
const LAST_TERMINAL_KEY = "tv_terminal_id";
const NUDGE_KEY = "tv_pwa_nudge_dismissed";
const ASSET_ORDER = [
  "Equities",
  "Crypto",
  "Fixed Income",
  "FX",
  "Commodities",
  "Multi-asset",
  "Private Markets",
  "Prediction Markets",
];

// The asset class is shown as the dropdown section header, so it doesn't need
// to repeat in the stack's display name. Strip a trailing " · <AssetClass>".
function stripClass(title, assetClass) {
  if (!title) return title;
  if (assetClass && title.endsWith(` · ${assetClass}`)) {
    return title.slice(0, -(` · ${assetClass}`.length)) || title;
  }
  return title;
}

function groupByCat(tools, catOrder) {
  const byCat = {};
  for (const t of tools || []) {
    const cat = t.category || "Other";
    (byCat[cat] = byCat[cat] || []).push(t);
  }
  const present = Object.keys(byCat);
  const ordered = [
    ...catOrder.filter((c) => byCat[c]),
    ...present.filter((c) => !catOrder.includes(c)),
  ];
  return ordered.map((cat) => ({ cat, tools: byCat[cat] }));
}

/**
 * Persistent "my stack" command bar — pinned to the very top of every page for
 * a logged-in user who has at least one saved stack. Switch between stacks,
 * launch any tool, or jump to the edit page. Hidden on the terminal edit page
 * (you're already managing that stack there) and on admin.
 */
export default function MyStackBar() {
  const loc = useLocation();
  const { user } = useAuthUser();
  const [stacks, setStacks] = useState(null); // null = loading/unknown, [] = none
  const [activeId, setActiveId] = useState(() => {
    try {
      // A window opened with ?stack=<id> is pinned to that stack (Bloomberg
      // launchpad — each window can show a different terminal).
      const pinned = new URLSearchParams(window.location.search).get("stack");
      if (pinned) return pinned;
      return localStorage.getItem(ACTIVE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [term, setTerm] = useState(null);
  const [catOrder, setCatOrder] = useState([]);
  const [switchOpen, setSwitchOpen] = useState(false);
  const switchRef = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef(null);
  const { canInstall, promptInstall } = usePWAInstall();
  const openMode = useOpenMode();
  const [workspace, setWorkspace] = useState(null); // {name, windows:[{slug,title,url}]}
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      return localStorage.getItem(NUDGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const dismissNudge = () => {
    setNudgeDismissed(true);
    try {
      localStorage.setItem(NUDGE_KEY, "1");
    } catch {
      /* storage blocked — non-fatal */
    }
  };

  const hidden =
    loc.pathname.startsWith("/terminal/") ||
    loc.pathname.startsWith("/admin") ||
    loc.pathname.startsWith("/build");

  // PWA / pinned-window fallback: in the installed app (or any window opened
  // with ?stack=<id>) the toolbar should appear from the pinned/last terminal
  // even without an active login — restoring the old standalone behavior.
  const stackParam = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("stack");
    } catch {
      return null;
    }
  }, []);
  const standalone = useMemo(() => isStandalone(), []);
  const pinnedId = useMemo(() => {
    if (stackParam) return stackParam;
    try {
      return localStorage.getItem(ACTIVE_KEY) || localStorage.getItem(LAST_TERMINAL_KEY) || null;
    } catch {
      return null;
    }
  }, [stackParam]);
  const allowFallback = (standalone || !!stackParam) && !!pinnedId;

  // Load the user's stacks (re-fetch on navigation so newly built stacks appear).
  // Falls back to the pinned terminal (PWA / ?stack window) when there's no
  // login or the user owns no stacks, so the launcher bar always persists.
  useEffect(() => {
    if (hidden) return undefined;
    let alive = true;
    const applyFallback = () => {
      if (!allowFallback) {
        if (alive) setStacks([]);
        return;
      }
      fetchTerminal(pinnedId)
        .then(
          (t) =>
            alive &&
            setStacks(
              t
                ? [
                    {
                      id: pinnedId,
                      title: t.title,
                      asset_class: t.asset_class,
                      count: (t.tools || []).length,
                    },
                  ]
                : [],
            ),
        )
        .catch(() => alive && setStacks([]));
    };
    if (user) {
      getMyTerminals()
        .then((s) => {
          if (!alive) return;
          if (s && s.length) setStacks(s);
          else applyFallback();
        })
        .catch(() => alive && applyFallback());
    } else {
      applyFallback();
    }
    fetchCategories()
      .then((d) => alive && setCatOrder(d.categories || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user, hidden, loc.pathname, allowFallback, pinnedId]);

  // Resolve the active stack + fetch its full tool list.
  useEffect(() => {
    if (!stacks || stacks.length === 0) {
      setTerm(null);
      return;
    }
    const id = activeId && stacks.some((s) => s.id === activeId) ? activeId : stacks[0].id;
    if (id !== activeId) {
      setActiveId(id);
      return;
    }
    let alive = true;
    fetchTerminal(id)
      .then((t) => alive && setTerm(t))
      .catch(() => alive && setTerm(null));
    return () => {
      alive = false;
    };
  }, [stacks, activeId]);

  useEffect(() => {
    const onDown = (e) => {
      if (switchRef.current && !switchRef.current.contains(e.target)) setSwitchOpen(false);
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Live stack-switch from the Cmd+K palette (same window, no reload).
  useEffect(() => {
    const onSet = (e) => {
      if (e.detail) setActiveId(e.detail);
    };
    window.addEventListener("tv:set-active-stack", onSet);
    return () => window.removeEventListener("tv:set-active-stack", onSet);
  }, []);

  // A tool was added to a stack elsewhere (e.g. the ⌘K palette) — re-fetch the
  // stack list so counts + the active terminal's launch menus refresh live.
  useEffect(() => {
    const onChanged = () => {
      if (!user) return;
      getMyTerminals()
        .then((s) => setStacks(s || []))
        .catch(() => {});
    };
    window.addEventListener("tv:stacks-changed", onChanged);
    return () => window.removeEventListener("tv:stacks-changed", onChanged);
  }, [user]);

  // Load the signed-in user's saved workspace (for the "open workspace" action).
  // Re-fetch when the user edits it (tv:workspace-changed event).
  useEffect(() => {
    if (!user) {
      setWorkspace(null);
      return undefined;
    }
    let alive = true;
    const load = () =>
      getWorkspace()
        .then((w) => alive && setWorkspace(w))
        .catch(() => alive && setWorkspace(null));
    load();
    window.addEventListener("tv:workspace-changed", load);
    return () => {
      alive = false;
      window.removeEventListener("tv:workspace-changed", load);
    };
  }, [user]);

  const groups = useMemo(() => groupByCat(term?.tools, catOrder), [term, catOrder]);

  const selectStack = (id) => {
    setActiveId(id);
    try {
      localStorage.setItem(ACTIVE_KEY, id);
    } catch {
      /* storage blocked — non-fatal */
    }
    setSwitchOpen(false);
  };

  if (hidden || !stacks || stacks.length === 0 || !term) return null;

  const activeTitle = stripClass(term.title, term.asset_class) || "my terminal";
  const shareLink = `${window.location.origin}/terminal/${activeId}`;
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("stack link copied");
    } catch {
      toast.error("could not copy");
    }
    setShareOpen(false);
  };

  // Bloomberg-style fan-out: open N desktop windows at once, each pinned to a
  // different stack and tiled across the screen. The window the user is in
  // becomes panel 1 (re-pinned to the active stack); the rest get the next
  // stacks in order, cycling if there are fewer stacks than windows.
  const openWindows = async (n) => {
    const origin = window.location.origin;
    const ordered = [activeId, ...stacks.map((s) => s.id).filter((id) => id !== activeId)];
    const items = [];
    for (let i = 0; i < n; i++) {
      items.push({ url: `${origin}/?stack=${ordered[i % ordered.length]}`, name: `tv_panel_${i}` });
    }
    const opened = await openManagedWindows(items);
    if (opened === 0) {
      toast.error(popupHelp(), { duration: POPUP_TOAST_DURATION });
    } else if (opened < n) {
      toast.warning(`Opened ${opened} of ${n}. ${popupHelp()}`, { duration: POPUP_TOAST_DURATION });
    } else {
      toast.success(`opened ${n} windows`);
    }
  };

  // Launch the user's saved workspace as tiled desktop windows (one per tool).
  const wsWindows = workspace?.windows || [];
  const openWorkspace = async () => {
    if (!wsWindows.length) return;
    const opened = await openManagedWindows(
      wsWindows.map((wn, i) => ({ url: wn.url, name: `tv_ws_${i}` })),
    );
    const n = wsWindows.length;
    if (opened === 0) {
      toast.error(popupHelp(), { duration: POPUP_TOAST_DURATION });
    } else if (opened < n) {
      toast.warning(`Opened ${opened} of ${n}. ${popupHelp()}`, { duration: POPUP_TOAST_DURATION });
    } else {
      toast.success(`opened workspace · ${n} windows`);
    }
  };

  const OPEN_MODE_LABELS = { same: "this window", newtab: "new tab", newwindow: "new window" };

  // Group the user's stacks by asset class for the two-level switcher
  // (asset class → stacks in that class), ordered canonically.
  const grouped = (() => {
    const m = {};
    for (const s of stacks) {
      const k = s.asset_class || "Other";
      (m[k] = m[k] || []).push(s);
    }
    const keys = Object.keys(m).sort((a, b) => {
      const ia = ASSET_ORDER.indexOf(a);
      const ib = ASSET_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
    return keys.map((k) => ({ assetClass: k, items: m[k] }));
  })();

  const leftSlot = (
    <div ref={switchRef} className="relative border-r border-black/15 shrink-0" data-testid="stackbar-switcher">
      <button
        type="button"
        onClick={() => setSwitchOpen((v) => !v)}
        className="px-3 py-2 font-bold text-[13px] hover:bg-black/[0.08] transition-colors flex items-center gap-1"
        data-testid="stackbar-switcher-btn"
        aria-haspopup="true"
        aria-expanded={switchOpen}
      >
        <span className="truncate max-w-[46vw] sm:max-w-none">{">"}_ {activeTitle}</span>
        {stacks.length > 1 && <span className="opacity-50 text-[10px] shrink-0">▾</span>}
      </button>
      {switchOpen && stacks.length > 1 && (
        <div
          className="absolute left-0 top-full min-w-[260px] max-h-[70vh] overflow-y-auto bg-white text-black border border-black z-50"
          data-testid="stackbar-switcher-menu"
        >
          {grouped.map((g) => (
            <div key={g.assetClass}>
              <div
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-black bg-[#f2f2f2] border-y border-black/15"
                data-testid={`stackbar-class-${g.assetClass}`}
              >
                {g.assetClass}
              </div>
              {g.items.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectStack(s.id)}
                  className={`block w-full text-left px-3 py-2 text-[12px] border-b border-black/10 hover:bg-[#1a73e8] hover:text-white ${
                    s.id === activeId ? "bg-[#e3e3e3]" : ""
                  }`}
                  data-testid={`stackbar-switch-${s.id}`}
                >
                  {stripClass(s.title, s.asset_class) || "untitled terminal"}{" "}
                  <span className="opacity-50 text-[10px] tabular-nums">({s.count})</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const rightSlot = (
    <>
      {canInstall && !nudgeDismissed && (
        <div
          className="flex items-center border-l border-black/15 shrink-0"
          data-testid="stackbar-install-nudge"
        >
          <button
            type="button"
            onClick={promptInstall}
            className="px-3 py-2 text-[12px] whitespace-nowrap hover:bg-[#1a73e8] hover:text-white transition-colors"
            data-testid="stackbar-install-btn"
            title="Install TerminalValue as a focused desktop / mobile app"
          >
            ↓ install app
          </button>
          <button
            type="button"
            onClick={dismissNudge}
            className="pr-2 pl-1 py-2 text-[13px] leading-none text-black/50 hover:text-black"
            data-testid="stackbar-install-dismiss"
            title="dismiss"
            aria-label="dismiss install prompt"
          >
            ×
          </button>
        </div>
      )}
      <div ref={shareRef} className="relative border-l border-black/15 shrink-0" data-testid="stackbar-more">
        <button
          type="button"
          onClick={() => setShareOpen((v) => !v)}
          className="px-3 py-2 text-[14px] leading-none whitespace-nowrap hover:bg-[#1a73e8] hover:text-white transition-colors"
          data-testid="stackbar-more-btn"
          aria-haspopup="true"
          aria-expanded={shareOpen}
          title="Windows, edit, or share this stack"
        >
          ⋯
        </button>
        {shareOpen && (
          <div
            className="absolute right-0 top-full min-w-[210px] bg-white text-black border border-black z-50"
            data-testid="stackbar-more-menu"
          >
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-black/50 border-b border-black/10">
              open tools in
            </div>
            {["same", "newtab", "newwindow"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setOpenMode(m)}
                className={`block w-full text-left px-3 py-2 text-[12px] border-b border-black/10 hover:bg-[#1a73e8] hover:text-white ${
                  openMode === m ? "bg-[#e3e3e3]" : ""
                }`}
                data-testid={`stackbar-openmode-${m}`}
              >
                {openMode === m ? "● " : "○ "}
                {OPEN_MODE_LABELS[m]}
              </button>
            ))}
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-black/50 border-b border-black/10">
              workspace
            </div>
            {wsWindows.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setShareOpen(false);
                  openWorkspace();
                }}
                className="block w-full text-left px-3 py-2 text-[12px] border-b border-black/10 hover:bg-[#1a73e8] hover:text-white"
                data-testid="stackbar-open-workspace"
                title="Open every workspace tool as its own tiled window"
              >
                ▶ open workspace ({wsWindows.length})
              </button>
            )}
            <Link
              to="/workspace"
              onClick={() => setShareOpen(false)}
              className="tv-menulink block w-full text-left px-3 py-2 text-[12px] border-b border-black/10"
              data-testid="stackbar-edit-workspace"
            >
              {wsWindows.length > 0 ? "edit workspace ↗" : "set up workspace ↗"}
            </Link>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-black/50 border-b border-black/10">
              open panels
            </div>
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setShareOpen(false);
                  openWindows(n);
                }}
                className="block w-full text-left px-3 py-2 text-[12px] border-b border-black/10 hover:bg-[#1a73e8] hover:text-white"
                data-testid={`stackbar-windows-${n}`}
                title="Open multiple stacks side-by-side, like Bloomberg panels"
              >
                ⊞ open {n} windows
              </button>
            ))}
            <Link
              to={`/terminal/${activeId}`}
              onClick={() => setShareOpen(false)}
              className="tv-menulink block w-full text-left px-3 py-2 text-[12px] border-b border-black/10"
              data-testid="stackbar-edit"
            >
              edit stack ↗
            </Link>
            <button
              type="button"
              onClick={copyShare}
              className="block w-full text-left px-3 py-2 text-[12px] border-b border-black/10 hover:bg-[#1a73e8] hover:text-white"
              data-testid="stackbar-share-copy"
            >
              copy link
            </button>
            <a
              href={`https://x.com/intent/post?text=${encodeURIComponent(
                `${activeTitle} — my investing tools terminal`,
              )}&url=${encodeURIComponent(shareLink)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setShareOpen(false)}
              className="tv-menulink block w-full text-left px-3 py-2 text-[12px]"
              data-testid="stackbar-share-x"
            >
              share on x ↗
            </a>
          </div>
        )}
      </div>
    </>
  );

  // Wrap launches so we still record per-user usage from the bar.
  const groupsWithTracking = groups.map((g) => ({
    ...g,
    tools: g.tools.map((t) => ({ ...t })),
  }));

  return (
    <div data-testid="my-stack-bar" onClickCapture={(e) => {
      const a = e.target.closest && e.target.closest("a[data-testid^='terminal-menu-item-']");
      if (a) {
        const id = a.getAttribute("data-testid").replace("terminal-menu-item-", "");
        recordLaunch(id);
      }
    }}>
      <TerminalMenuBar groups={groupsWithTracking} categoryVocab={catOrder} clusters={term?.clusters} leftSlot={leftSlot} rightSlot={rightSlot} openMode={openMode} />
    </div>
  );
}
